import React, { useEffect, useState } from "react";
import { useForm, SubmitHandler } from "react-hook-form";
import { getUserDetails, updateUserInfo, verifyToken } from "../services/User_service";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "../css/login.css";
import { FaUser, FaLock, FaPhone, FaGlobe, FaMapMarkerAlt, FaBuilding } from "react-icons/fa";
import logo from "../image/logo.png";
import bannerImage from "../image/image.gif";
import { useNavigate } from "react-router-dom";
import { saveUserToLocalStorage } from "../services/Login_service";

type Inputs = {
  firstname: string;
  name: string;
  adresse: string;
  country: string;
  number: string;
  password: string;
  confirmPassword: string;
  postal_code: string;
  civility: string;
  status: string;
};

const CompleteRegistration: React.FC = () => {
  const { register, handleSubmit, setValue, formState: { errors } } = useForm<Inputs>();
  const [loading, setLoading] = useState<boolean>(false);
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [userId, setUserId] = useState<string | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const token = urlParams.get("token");

    if (token) {
      verifyToken(token)
        .then((response: any) => {
          const userId = response.user.id;
          saveUserToLocalStorage(response.user, response.token)
          setUserId(userId);
          setIsAuthenticated(true);
          fetchUser(userId, response.token);
        })
        .catch(() => toast.error("Lien invalide ou expiré."));
    } else {
      toast.error("Aucun token fourni.");
    }
  }, []);

  const fetchUser = async (userId: string, token: string) => {
    try {
      const user: any = await getUserDetails();
      localStorage.setItem('role', user.role);
      localStorage.setItem('reference', user.reference);
      localStorage.setItem('username', `${user.firstname} ${user.name}`);
      if (user) {
        Object.keys(user).forEach((key) => {
          setValue(key as keyof Inputs, (user as any)[key] || "");
        });
      }
    } catch (error) {
      toast.error("Erreur lors de la récupération des informations.");
    }
  };

  const onSubmit: SubmitHandler<Inputs> = async (data) => {
    if (data.password && data.password !== data.confirmPassword) {
      toast.error("Les mots de passe ne correspondent pas.");
      return;
    }

    if (!userId) {
      toast.error("Utilisateur non authentifié.");
      return;
    }

    setLoading(true);

    try {
      data.status = "active";
      await updateUserInfo(parseInt(userId), data);
      toast.success("Informations mises à jour avec succès !");
      navigate("/home");
    } catch (error) {
      toast.error("Erreur lors de la mise à jour des informations.");
    } finally {
      setLoading(false);
    }
  };

  if (!isAuthenticated) {
    return (
      <div className="degrade-vertical big-height d-flex justify-content-center align-items-center">
        <p>Veuillez patienter, vous êtes en train de vous connecter...</p>
      </div>
    );
  }

  return (
    <>
      <ToastContainer />
      <div className="container-register">
        <div className="row">
          {/* Right Section - Form */}
          <div className="col-12 col-md-6 section-form">
            <div className="text-center">
              <img src={logo} className="my-3 logo-1" alt="logo" />
              <h2>Modifier vos informations</h2>
            </div>
            <form onSubmit={handleSubmit(onSubmit)}>
              <div className="row">
                {/* Civility, First Name and Last Name */}
                <div className="col-12 col-md-4 mb-3">
                  <label htmlFor="civility" className="form-label">Civilité</label>
                  <div className="input-group">
                    <select className="form-control" id="civility" {...register("civility", { required: true })}>
                      <option value="monsieur">Monsieur</option>
                      <option value="madame">Madame</option>
                    </select>
                  </div>
                </div>
                <div className="col-12 col-md-4 mb-3">
                  <label htmlFor="firstname" className="form-label">Prénom</label>
                  <div className="input-group">
                    <span className="input-group-text"><FaUser /></span>
                    <input
                      type="text"
                      className="form-control"
                      id="firstname"
                      placeholder="Prénom"
                      {...register("firstname", { required: true })}
                    />
                  </div>
                </div>
                <div className="col-12 col-md-4 mb-3">
                  <label htmlFor="name" className="form-label">Nom</label>
                  <div className="input-group">
                    <span className="input-group-text"><FaUser /></span>
                    <input
                      type="text"
                      className="form-control"
                      id="name"
                      placeholder="Nom"
                      {...register("name", { required: true })}
                    />
                  </div>
                </div>

                {/* Address, Country, and Phone */}
                <div className="col-12 col-md-6 mb-3">
                  <label htmlFor="adresse" className="form-label">Adresse</label>
                  <div className="input-group">
                    <span className="input-group-text"><FaMapMarkerAlt /></span>
                    <input
                      type="text"
                      className="form-control"
                      id="adresse"
                      placeholder="Adresse"
                      {...register("adresse", { required: true })}
                    />
                  </div>
                </div>
                <div className="col-12 col-md-6 mb-3">
                  <label htmlFor="country" className="form-label">Pays</label>
                  <div className="input-group">
                    <span className="input-group-text"><FaGlobe /></span>
                    <input
                      type="text"
                      className="form-control"
                      id="country"
                      placeholder="Pays"
                      {...register("country", { required: true })}
                    />
                  </div>
                </div>
                <div className="col-12 col-md-6 mb-3">
                  <label htmlFor="number" className="form-label">Téléphone</label>
                  <div className="input-group">
                    <span className="input-group-text"><FaPhone /></span>
                    <input
                      type="text"
                      className="form-control"
                      id="number"
                      placeholder="Téléphone"
                      {...register("number", { required: true })}
                    />
                  </div>
                </div>

                {/* Postal Code */}
                <div className="col-12 col-md-6 mb-3">
                  <label htmlFor="postal_code" className="form-label">Code postal</label>
                  <div className="input-group">
                    <span className="input-group-text"><FaBuilding /></span>
                    <input
                      type="text"
                      className="form-control"
                      id="postal_code"
                      placeholder="Code postal"
                      {...register("postal_code", { required: true })}
                    />
                  </div>
                </div>

                {/* Password and Confirm Password */}
                <div className="col-12 col-md-6 mb-3">
                  <label htmlFor="password" className="form-label">Mot de passe</label>
                  <div className="input-group">
                    <span className="input-group-text"><FaLock /></span>
                    <input
                      type="password"
                      className="form-control"
                      id="password"
                      placeholder="Mot de passe"
                      {...register("password", { required: true })}
                    />
                  </div>
                </div>
                <div className="col-12 col-md-6 mb-3">
                  <label htmlFor="confirmPassword" className="form-label">Confirmer mot de passe</label>
                  <div className="input-group">
                    <span className="input-group-text"><FaLock /></span>
                    <input
                      type="password"
                      className="form-control"
                      id="confirmPassword"
                      placeholder="Confirmer mot de passe"
                      {...register("confirmPassword", { required: true })}
                    />
                  </div>
                </div>

                
              </div>

              {/* Submit Button */}
              <button type="submit" className="btn btn-primary w-100 mt-4">
                {loading ? "Mise à jour..." : "Mettre à jour"}
              </button>
            </form>
          </div>

          {/* Left Section */}
          <div className="col-12 col-md-6 d-flex align-items-center">
            <div>
              <img src={bannerImage} alt="gif" className="img-fluid w-100" />
              <h1 className="mt-4">Mettez à jour vos informations</h1>
              <p>Assurez-vous que vos informations sont à jour pour une expérience fluide sur notre plateforme.</p>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default CompleteRegistration;
